import Badge from '@repo/ui/badge'
import { LocationFourIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { getCseByCode } from '../../../../api/cse/get-cse-by-code'
import { getEnrollmentPropositionPricing } from '../../../../api/enrollments/get-enrollment-proposition-summary'
import { EnrollmentPropositionInput } from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition-input'
import { mapEnrollmentPropositionInputToRequest } from '../utils/enrollments-utils'
import EnrollmentPropositionForm, {
  EnrollmentPropositionFormProps
} from './enrollment-proposition-form'

export type Props = EnrollmentPropositionFormProps & {
  isOpen: boolean
  mode?: 'parent' | 'admin'
  onClose: () => void
}

export type EnrollmentPropositionFormModalRef = {
  setIndex: (index: number) => void // Expose a method to change the index
}

const EnrollmentPropositionFormModal = ({ isOpen, onClose, mode = 'admin', ...props }: Props) => {
  const [input, setInput] = useState<EnrollmentPropositionInput | undefined>(props.defaultValues)
  const { declaredFriends: _declaredFriends, ...rest } = input ?? {}
  const wasOpen = useRef(isOpen)

  const { data, isFetching } = useQuery({
    queryKey: ['enrollmentPropositionSummary', { ...rest }],
    queryFn: () => {
      if (rest)
        return getEnrollmentPropositionPricing(mapEnrollmentPropositionInputToRequest(rest, true))
    },
    enabled: isOpen && !!rest && !!rest.journey && mode === 'admin'
  })

  const { data: cse, refetch } = useQuery({
    queryKey: ['cse', input?.cseCode, 'BASIC'],
    queryFn: async () => {
      if (!input?.cseCode) throw new Error('Identifiant de CSE invalide')
      return getCseByCode(input?.cseCode, 'BASIC')
    },
    enabled: !!input?.cseCode
  })

  useEffect(() => {
    if (isOpen && !wasOpen.current) setInput(props.defaultValues)
    wasOpen.current = isOpen
  }, [isOpen, props.defaultValues])

  return (
    <Modal
      isOpen={isOpen}
      title={"Sélectionner un séjour pour la demande d'inscription"}
      icon={LocationFourIcon}
      size={'large'}
      hideActions
      disableOutSideClick
      onCancel={onClose}
      headerChildren={
        <div className="flex flex-row gap-2">
          <Badge
            variant="warning"
            loading={isFetching}
            text={
              <Typography variant="caption">
                Prix total{' '}
                <span className="font-light">
                  <small>(hors frais de dossier)</small>
                </span>
                : {data?.total ?? 0} €
              </Typography>
            }
          />

          {cse && (
            <Badge
              variant="grey"
              text={<Typography variant="caption">CSE : {cse.label}</Typography>}
            />
          )}
        </div>
      }
    >
      {isOpen && (
        <EnrollmentPropositionForm
          {...props}
          defaultValues={props.defaultValues}
          onInputChange={async (data) => {
            setInput(data)
            if (data.cseCode !== input?.cseCode) await refetch()
          }}
        />
      )}
    </Modal>
  )
}

export default EnrollmentPropositionFormModal
